import { pxToRem } from '@modernhealth/vital-components';
import styled from '@emotion/styled';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import videojs, { VideoJsPlayer, VideoJsPlayerOptions } from 'video.js';
import 'video.js/dist/video-js.css';

// See https://www.notion.so/modernhealth/Web-Video-Spike-19b9d00b96784605990ff91147d9de02
// for reasoning behind choosing video.js as the new VideoPlayer solution.

interface VideoJsSource {
    src: string;
    type: string;
}

export interface VideoJSTextTrack {
    kind: VideoJsTextTrackKind;
    label: string;
    srcLang: string;
    src: string;
    default: boolean;
}

interface VideoPlayerProps {
    source: VideoJsSource;
    title: string;
    poster?: string;
    children: React.ReactNode;
}

export enum VideoJsTextTrackKind {
    Subtitles = 'subtitles',
    Captions = 'captions',
    Descriptions = 'descriptions',
    Chapters = 'chapters',
    Metadata = 'metadata',
}

export enum VideoPlayerSupportedFileFormats {
    MP4 = 'video/mp4',
}

const PLAYER_BORDER_RADIUS_REM = pxToRem(6);
const PLAY_BUTTON_RADIUS_REM = 1.75;

const StyledVideo = styled.video({
    '.vjs-control-bar': {
        borderBottomLeftRadius: PLAYER_BORDER_RADIUS_REM,
        borderBottomRightRadius: PLAYER_BORDER_RADIUS_REM,
    },
    '.video-js.mh-player': {
        borderRadius: PLAYER_BORDER_RADIUS_REM,
    },
    '.vjs-tech': {
        borderRadius: PLAYER_BORDER_RADIUS_REM,
    },
    '.vjs-poster': {
        borderRadius: PLAYER_BORDER_RADIUS_REM,
    },
    '.vjs-big-play-button.round-white': {
        border: 'none',
        backgroundColor: 'white',
        borderRadius: '50%',
        height: `${2 * PLAY_BUTTON_RADIUS_REM}rem`,
        width: `${2 * PLAY_BUTTON_RADIUS_REM}rem`,
        marginLeft: `${-PLAY_BUTTON_RADIUS_REM}rem`,
        marginTop: `${(-PLAY_BUTTON_RADIUS_REM / 4) * 3}rem`,
    },
    '.vjs-big-play-button .vjs-icon-placeholder:before': {
        color: 'black',
        marginTop: `${PLAY_BUTTON_RADIUS_REM / 4}rem`,
    },
    // https://docs.videojs.com/tutorial-text-tracks.html#text-track-settings
    '.vjs-texttrack-settings': {
        display: 'none',
    },
    borderRadius: PLAYER_BORDER_RADIUS_REM,
    // backgroundColor of the videoPlayer is black by default; with our borderRadius styling,
    // the background peeks out slightly and causes a noticeable shadow behind the player
    // We use `inherit` here rather than white since it is more likely to support default
    // as well as "dark mode" browser variants.
    backgroundColor: 'inherit',
});

const baseOptions: VideoJsPlayerOptions = {
    controls: true,
    fluid: true,
    controlBar: {
        volumePanel: {
            inline: false,
        },
        pictureInPictureToggle: false,
    },
    inactivityTimeout: 750,
};

/**
 * useEffect triggers after renders, so updating a ref in a useEffect guarantees the returned value
 * will always be one cycle behind.
 * https://blog.logrocket.com/how-to-get-previous-props-state-with-react-hooks/ has a great breakdown
 */
function usePrevious<T>(value: T) {
    const ref = useRef<T>();
    useEffect(() => {
        ref.current = value;
    });
    return ref.current;
}

/**
 * Utility function to manage the initialization and lifecycle of the video.js Player
 * @returns The current video.js Player object and a callback to pass to the <video> element's ref property
 */
function usePlayer(): readonly [VideoJsPlayer | null, (videoElement: HTMLVideoElement) => void] {
    const [player, setPlayer] = useState<VideoJsPlayer | null>(null);
    const previousPlayer = usePrevious(player);
    const videoElementRef = useCallback((videoElement: HTMLVideoElement) => {
        if (!videoElement) return;
        // https://github.com/videojs/video.js/blob/774f9e7f459df1df532ddab1b8b20117509c7c1e/src/js/video.js#L251
        // Additionally, added this seemingly unnecessary guard because tests were indicating it was possible for videojs.getPlayer
        // to be undefined, failing with "videojs.getPlayer is not a function"
        let player = videojs?.getPlayer?.(videoElement);
        if (player) {
            setPlayer(player);
            return;
        }

        // If the <video> element we're trying to attach videoJS to exists, initialize the videojs instance
        // and save a reference to the VideoJsPlayer
        player = videojs(videoElement, baseOptions);
        // https://docs.videojs.com/tutorial-skins.html#add-a-custom-class-to-the-player
        const bigPlayButton = player?.getChild('bigPlayButton');
        bigPlayButton?.addClass('round-white');
        setPlayer(player);
    }, []);

    /* After the initial invocation of a useEffect, if the effect is going to trigger again, the cleanup function
     * triggers immediately before the main body contents of the useEffect
     * Since we want this cleanup to be run on a different set of conditions than when the player is set, we split it out
     * into a separate useEffect to ensure it is cleaned up only after it is guaranteed that we aren't using it anymore
     * We cannot do this immediately after setPlayer() because the state update and subsequent render cycle is not
     * guaranteed to finish before the cleanup is attempted. Instead, by splitting cleanup into this separate useEffect,
     * we guarantee the setPlayer state update and subsequent render cycle have completed before this cleanup is called.
     */
    useEffect(() => {
        return () => {
            if (player !== previousPlayer) {
                previousPlayer?.dispose();
            }
        };
    }, [player, previousPlayer]);

    return [player, videoElementRef] as const;
}

/**
 * Utility context to pass the video.js Player across subcomponents (e.g. with VideoPlayerTextTrack components).
 */
const PlayerContext = React.createContext<{ player: videojs.Player | null }>({ player: null });

interface VideoPlayerTextTrackProps {
    track: VideoJSTextTrack;
}

/**
 * A background component to manage the lifecycle of a remoteTextTrack within a video.js Player
 * @param param0 The textTrack to add to a video.js Player
 */
export const VideoPlayerTextTrack: React.FC<VideoPlayerTextTrackProps> = ({
    track,
}: VideoPlayerTextTrackProps) => {
    const cx = React.useContext(PlayerContext);
    const { player } = cx;

    useEffect(() => {
        if (!player) return;
        const textTrack = player.addRemoteTextTrack(
            {
                kind: track.kind,
                label: track.label,
                src: track.src,
                srclang: track.srcLang,
                default: track.default,
            },
            true,
        );

        return () => {
            player.removeRemoteTextTrack(textTrack);
        };
    }, [player, track.src, track.srcLang, track.label, track.kind, track.default]);

    return null;
};

type VideoPlayerTextTrackListProps = Pick<VideoPlayerProps, 'children'>;

const VideoPlayerTextTrackList: React.FC<VideoPlayerTextTrackListProps> = (
    props: VideoPlayerTextTrackListProps,
) => {
    const { children } = props;
    const textTracks = React.Children.toArray(children).map(child => {
        if (typeof child === 'object' && 'type' in child) {
            if (child.type === VideoPlayerTextTrack) {
                return <VideoPlayerTextTrack {...child.props} key={child.key} />;
            }
        }

        return null;
    });
    return <>{textTracks}</>;
};

export const VideoPlayer: React.FC<VideoPlayerProps> = (props: VideoPlayerProps) => {
    const { source, poster, title, children } = props;
    const [player, videoElementRef] = usePlayer();

    useEffect(() => {
        if (!player) return;

        if (poster) player.poster(poster);

        player.src([{ src: source.src, type: source.type }]);
    }, [player, poster, source.src, source.type]);

    return (
        <div vjs-data-element="true">
            <StyledVideo
                ref={videoElementRef}
                className="video-js vjs-big-play-centered"
                title={title}
            />
            <PlayerContext.Provider value={{ player }}>
                <VideoPlayerTextTrackList>{children}</VideoPlayerTextTrackList>
            </PlayerContext.Provider>
        </div>
    );
};

export default VideoPlayer;
